import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/cs';
import 'dayjs/locale/sk';
import 'dayjs/locale/hu';
import 'dayjs/locale/de';
import 'dayjs/locale/ro';
import 'dayjs/locale/it';

import { accuracyQuality } from '@ngsos/portal-core/incidents';

import type { PanelItemColor, PositionSource } from './types';
import { NgBadge } from './badges/NgBadge';
import { positionSourceIcon } from './PositionSourceBadge';

dayjs.extend(relativeTime);

export type PositionSourceBadgeWithInfoProps = {
  positionSource: PositionSource;
  timestamp: Date;
  accuracy: number | null;
};

export function accuracyColor(accuracy: number): PanelItemColor {
  return accuracyQuality(accuracy);
}

function useRelativeTime(date: Date, language: string): string {
  const twoLetterLanguage = language.split('-')[0] ?? 'en';
  const format = () => dayjs(date).locale(twoLetterLanguage).fromNow();
  const [relative, setRelative] = useState(format);

  useEffect(() => {
    setRelative(format());
    const interval = setInterval(() => setRelative(format()), 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date.getTime(), twoLetterLanguage]);

  return relative;
}

export function PositionSourceBadgeWithInfo({ positionSource, timestamp, accuracy }: PositionSourceBadgeWithInfoProps) {
  const { t, i18n } = useTranslation();
  const relative = useRelativeTime(timestamp, i18n.language || 'en');

  const accuracyFormatted =
    accuracy == null
      ? null
      : `${new Intl.NumberFormat(i18n.language || 'en', { maximumFractionDigits: 0 }).format(accuracy)} m`;

  const effectiveText = (() => {
    switch (positionSource) {
      case 'mobileApp':
        return t('PositionSource_MobileApp_Label');
      case 'mobileAppSms':
        return t('PositionSource_MobileAppSms_Label');
      case 'webApp':
        return t('PositionSource_WebApp_Label');
      case 'manual':
        return t('PositionSource_Manual_Label');
      case 'aml':
        return t('PositionSource_Aml_Label');
      case 'appleEed':
        return t('PositionSource_AppleEed_Label');
      case 'googleEls':
        return t('PositionSource_GoogleEls_Label');
      default:
        return '—';
    }
  })();

  return (
    <div className="d-flex flex-column">
      <span className="medium">{effectiveText}</span>
      <div className="d-flex flex-row gap-2 align-items-center">
        <img src={positionSourceIcon(positionSource)} alt="" className="mr-2" style={{ height: '28px' }} />
        <div
          className="d-flex flex-column small"
          title={accuracyFormatted == null ? undefined : `${t('Incident_InfoPanel_Accuracy')}: ${accuracyFormatted}`}
        >
          {positionSource !== 'manual' && accuracy !== null && accuracyFormatted !== null && (
            <NgBadge
              color={accuracyColor(accuracy)}
              value={accuracyFormatted}
              label={t('Incident_InfoPanel_Accuracy')}
            />
          )}
          <span className="text-muted">{relative}</span>
        </div>
      </div>
    </div>
  );
}
